import { useMemo } from "react";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import {
  CHART_TOOLTIP_CURSOR,
  ChartTooltipCard,
  formatShare,
} from "@/lib/dashboard/shared/ChartTooltip";
import { DONUT_COLORS } from "@/lib/dashboard/chartColors";
import { useI18n } from "@/hooks/useI18n";
import { formatPlanAmount } from "@/lib/builder/utils/payment-format";
import {
  DetailCashflowPoint,
  DetailDestination,
} from "@/integrations/supabase/rpc-types";

const PAID_COLOR = "#10b981";
const EXPECTED_COLOR = "#f59e0b";

/** "2026-03" -> "mar 26". L'anno serve: le rate scavallano spesso il capodanno. */
function formatMonth(month: string): string {
  const [year, m] = month.split("-");
  const date = new Date(Number(year), Number(m) - 1, 1);
  if (Number.isNaN(date.getTime())) return month;
  return date
    .toLocaleDateString("it-IT", { month: "short", year: "2-digit" })
    .replace(".", "");
}

interface CashflowChartProps {
  data: DetailCashflowPoint[];
}

/**
 * Incassato e atteso mese per mese, sulle stesse colonne.
 *
 * L'incassato e' collocato sul mese in cui il denaro e' entrato (`paid_at`), l'atteso su
 * quello della scadenza (`due_date`): e' la differenza fra "cosa e' successo" e "cosa deve
 * succedere", e leggerli affiancati dice in un colpo se il cliente e' avanti o indietro.
 */
export function DetailCashflowChart({ data }: CashflowChartProps) {
  const { t } = useI18n();

  const chartData = useMemo(
    () =>
      data.map((point) => ({
        month: formatMonth(point.month),
        paid: point.paid,
        expected: point.expected,
      })),
    [data]
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          {t("dashboard.overview.detail.cashflowTitle")}
          <InfoTooltip text={t("dashboard.overview.detail.cashflowInfo")} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-44 text-sm text-gray-400">
            {t("dashboard.overview.detail.cashflowEmpty")}
          </div>
        ) : (
          <>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -12 }}>
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    width={56}
                    tickFormatter={(value: number) =>
                      formatPlanAmount(value, { showSymbol: false })
                    }
                  />
                  <RechartsTooltip
                    cursor={CHART_TOOLTIP_CURSOR}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const point = payload[0].payload as {
                        paid: number;
                        expected: number;
                      };
                      /*
                        Le due serie si leggono sempre insieme, anche quando il mouse e'
                        su una sola colonna: e' il confronto fra incassato e atteso a
                        dire se il cliente e' avanti o indietro, non il singolo numero.
                      */
                      return (
                        <ChartTooltipCard
                          title={label as string}
                          rows={[
                            {
                              label: t("dashboard.overview.detail.cashflowPaid"),
                              value: formatPlanAmount(point.paid),
                              color: PAID_COLOR,
                            },
                            {
                              label: t("dashboard.overview.detail.cashflowExpected"),
                              value: formatPlanAmount(point.expected),
                              color: EXPECTED_COLOR,
                            },
                          ]}
                        />
                      );
                    }}
                  />
                  <Bar dataKey="paid" fill={PAID_COLOR} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="expected" fill={EXPECTED_COLOR} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: PAID_COLOR }}
                />
                {t("dashboard.overview.detail.cashflowPaid")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: EXPECTED_COLOR }}
                />
                {t("dashboard.overview.detail.cashflowExpected")}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface DestinationsChartProps {
  data: DetailDestination[];
  /** "Destinazioni del cliente" o "del consulente": lo sa solo la scheda che ospita. */
  title: string;
  info: string;
}

/** Le destinazioni del soggetto: dice a colpo d'occhio che viaggi tratta. */
export function DetailDestinationsChart({ data, title, info }: DestinationsChartProps) {
  const { t } = useI18n();

  const pieData = useMemo(
    () => data.map((d) => ({ name: d.destination, value: d.count })),
    [data]
  );

  const destinationsTotal = useMemo(
    () => pieData.reduce((sum, d) => sum + d.value, 0),
    [pieData]
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          {title}
          <InfoTooltip text={info} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {pieData.length === 0 ? (
          <div className="flex items-center justify-center h-44 text-sm text-gray-400">
            {t("dashboard.overview.detail.destinationsEmpty")}
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="h-36 w-36 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={40}
                    outerRadius={62}
                    paddingAngle={2}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={DONUT_COLORS[index % DONUT_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const slice = payload[0].payload as {
                        name: string;
                        value: number;
                        fill?: string;
                      };
                      return (
                        <ChartTooltipCard
                          title={slice.name}
                          rows={[
                            {
                              label: t("dashboard.overview.detail.destinationsTooltipLabel"),
                              value: String(slice.value),
                              color: slice.fill,
                            },
                          ]}
                          footer={t("dashboard.overview.chartTooltip.share", {
                            percent: formatShare(slice.value, destinationsTotal),
                          })}
                        />
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="space-y-1.5 text-sm flex-1 min-w-0">
              {pieData.slice(0, 6).map((d, index) => (
                <li key={d.name} className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }}
                  />
                  <span className="truncate text-gray-700">{d.name}</span>
                  <span className="ml-auto text-gray-500 flex-shrink-0">{d.value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
